import React, { useState } from 'react';
import { Briefcase, Users, Heart, TrendingUp, Gift, Coffee, Award, Sparkles, Upload, X, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const Career = () => {
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    mobile: '',
    address: '',
    experience: '',
    noticePeriod: '',
    currentCompany: '',
    currentDesignation: '',
    expectedSalary: '',
    resume: null,
    photo: null
  });

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  const benefits = [
    {
      icon: <Heart className="h-8 w-8" />,
      title: "Health & Wellness",
      description: "Comprehensive health insurance coverage for you and your family",
      color: "from-red-500 to-pink-500"
    },
    {
      icon: <TrendingUp className="h-8 w-8" />,
      title: "Career Growth",
      description: "Regular training programs and clear career progression paths",
      color: "from-blue-500 to-cyan-500"
    },
    {
      icon: <Gift className="h-8 w-8" />,
      title: "Competitive Salary",
      description: "Industry-leading compensation packages with performance bonuses",
      color: "from-purple-500 to-indigo-500"
    },
    {
      icon: <Coffee className="h-8 w-8" />,
      title: "Work-Life Balance",
      description: "Flexible work hours and remote work opportunities",
      color: "from-orange-500 to-yellow-500"
    },
    {
      icon: <Award className="h-8 w-8" />,
      title: "Recognition & Rewards",
      description: "Monthly rewards and annual awards for outstanding performance",
      color: "from-green-500 to-emerald-500"
    },
    {
      icon: <Users className="h-8 w-8" />,
      title: "Team Culture",
      description: "Collaborative environment with regular team events and activities",
      color: "from-pink-500 to-rose-500"
    }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e, fieldName) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        [fieldName]: file
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const formDataToSend = new FormData();
      Object.keys(formData).forEach(key => {
        if (formData[key]) {
          formDataToSend.append(key, formData[key]);
        }
      });

      const response = await fetch(`${API_BASE_URL}/api/career/apply`, {
        method: 'POST',
        body: formDataToSend
      });

      if (response.ok) {
        toast.success('Application submitted successfully! We will contact you soon.');
        setShowForm(false);
        setFormData({
          fullName: '',
          email: '',
          mobile: '',
          address: '',
          experience: '',
          noticePeriod: '',
          currentCompany: '',
          currentDesignation: '',
          expectedSalary: '',
          resume: null,
          photo: null
        });
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to submit application');
      }
    } catch (error) {
      console.error('Error submitting application:', error);
      toast.error('Failed to submit application. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 py-20">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1572021335469-31706a17aaef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxvZmZpY2UlMjB0ZWFtfGVufDB8fHx8MTc2MzMwMDk2MXww&ixlib=rb-4.1.0&q=85)'
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-900/90 via-teal-900/85 to-cyan-900/90"></div>
        <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:20px_20px]"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full mb-6">
              <Sparkles className="h-5 w-5 text-yellow-300" />
              <span className="text-white font-semibold">Join Our Growing Team</span>
            </div>
            
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Build Your Career at <span className="text-yellow-300">Netix Fibernet</span>
            </h1>
            
            <p className="text-xl text-white/90 mb-8 leading-relaxed">
              Join India's fastest-growing fiber internet service provider. We're looking for passionate individuals who want to make a difference in connecting people to the future.
            </p>
            
            <Button 
              onClick={() => setShowForm(true)}
              className="px-8 py-6 text-lg font-bold bg-yellow-400 hover:bg-yellow-500 text-gray-900 shadow-2xl hover:shadow-yellow-400/50 transition-all transform hover:scale-105"
            >
              <Briefcase className="mr-2 h-5 w-5" />
              Join Our Waitlist
            </Button>
          </div>
        </div>

        {/* Decorative Elements */}
        <div className="absolute top-10 left-10 w-20 h-20 bg-yellow-400/20 rounded-full blur-2xl"></div>
        <div className="absolute bottom-10 right-10 w-32 h-32 bg-cyan-400/20 rounded-full blur-3xl"></div>
      </div>

      {/* Why Join Us Banner */}
      <div className="relative bg-gradient-to-r from-purple-600 to-indigo-600 py-12">
        {/* Background Pattern */}
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-10"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1703355685952-03ed19f70f51?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBvZmZpY2UlMjBtZWV0aW5nfGVufDB8fHx8MTc2MzMwMDk3MXww&ixlib=rb-4.1.0&q=85)'
          }}
        ></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid md:grid-cols-3 gap-6 text-center">
            <div className="text-white">
              <div className="text-4xl font-bold mb-2">5000+</div>
              <div className="text-purple-200">Happy Customers</div>
            </div>
            <div className="text-white">
              <div className="text-4xl font-bold mb-2">50+</div>
              <div className="text-purple-200">Team Members</div>
            </div>
            <div className="text-white">
              <div className="text-4xl font-bold mb-2">10+</div>
              <div className="text-purple-200">Cities Covered</div>
            </div>
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div className="py-20 relative">
        {/* Background Image with Overlay */}
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-5"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1758518729908-d4220a678d81?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHw0fHx3b3JrcGxhY2UlMjBjb2xsYWJvcmF0aW9ufGVufDB8fHx8MTc2MzMwMDk2Nnww&ixlib=rb-4.1.0&q=85)'
          }}
        ></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Why Join <span className="text-emerald-600">Netix Fibernet?</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              We believe in creating an environment where our employees thrive and grow
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {benefits.map((benefit, index) => (
              <div 
                key={index}
                className="group relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100"
              >
                <div className={`inline-flex p-4 rounded-xl bg-gradient-to-r ${benefit.color} text-white mb-4 group-hover:scale-110 transition-transform`}>
                  {benefit.icon}
                </div>
                
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {benefit.title}
                </h3>
                
                <p className="text-gray-600 leading-relaxed">
                  {benefit.description}
                </p>

                <div className={`absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r ${benefit.color} transform scale-x-0 group-hover:scale-x-100 transition-transform rounded-b-2xl`}></div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Inspiring Team Banner */}
      <div className="relative h-80 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.pexels.com/photos/7437499/pexels-photo-7437499.jpeg)'
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-900/80 to-cyan-900/80 flex items-center justify-center">
          <div className="text-center text-white px-4">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Be Part of Something <span className="text-yellow-300">Extraordinary</span>
            </h2>
            <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto">
              Join a team that's revolutionizing India's internet connectivity
            </p>
          </div>
        </div>
      </div>

      {/* Work Environment Section */}
      <div className="relative bg-gradient-to-br from-gray-900 to-gray-800 py-20">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1568992687947-868a62a9f521?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwyfHxvZmZpY2UlMjB0ZWFtfGVufDB8fHx8MTc2MzMwMDk2MXww&ixlib=rb-4.1.0&q=85)'
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900/95 to-gray-800/95"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-white mb-4">
                Our <span className="text-emerald-400">Work Environment</span>
              </h2>
              <p className="text-xl text-gray-300">
                Experience a workplace that values innovation, collaboration, and growth
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <h3 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                  <CheckCircle className="h-6 w-6 text-emerald-400" />
                  Innovation First
                </h3>
                <p className="text-gray-300 leading-relaxed">
                  We encourage creative thinking and provide resources to bring your innovative ideas to life. Your voice matters here.
                </p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <h3 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                  <CheckCircle className="h-6 w-6 text-cyan-400" />
                  Learning Culture
                </h3>
                <p className="text-gray-300 leading-relaxed">
                  Continuous learning opportunities through workshops, certifications, and mentorship programs to enhance your skills.
                </p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <h3 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                  <CheckCircle className="h-6 w-6 text-purple-400" />
                  Inclusive Environment
                </h3>
                <p className="text-gray-300 leading-relaxed">
                  We celebrate diversity and foster an inclusive culture where everyone feels valued and respected.
                </p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <h3 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                  <CheckCircle className="h-6 w-6 text-yellow-400" />
                  Modern Infrastructure
                </h3>
                <p className="text-gray-300 leading-relaxed">
                  State-of-the-art office facilities with latest technology and tools to help you perform at your best.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 py-20">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Start Your Journey?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Don't miss this opportunity to be part of something amazing. Join our waitlist today!
          </p>
          <Button 
            onClick={() => setShowForm(true)}
            className="px-8 py-6 text-lg font-bold bg-white hover:bg-gray-100 text-emerald-600 shadow-2xl hover:shadow-white/50 transition-all transform hover:scale-105"
          >
            <Briefcase className="mr-2 h-5 w-5" />
            Apply Now
          </Button>
        </div>
      </div>

      {/* Application Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-4xl w-full my-4 shadow-2xl max-h-[95vh] flex flex-col">
            <div className="bg-gradient-to-r from-emerald-600 to-cyan-600 p-5 rounded-t-2xl flex justify-between items-center flex-shrink-0">
              <div>
                <h3 className="text-xl font-bold text-white">Join Our Waitlist</h3>
                <p className="text-white/90 text-sm mt-1">Fill in your details and we'll get back to you soon</p>
              </div>
              <button
                onClick={() => setShowForm(false)}
                className="p-2 hover:bg-white/20 rounded-full transition-colors"
              >
                <X className="h-6 w-6 text-white" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 overflow-y-auto flex-1">
              <div className="grid md:grid-cols-2 gap-4">
                {/* Full Name */}
                <div className="col-span-2">
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition-all text-sm"
                    placeholder="Enter your full name"
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition-all text-sm"
                    placeholder="your.email@example.com"
                  />
                </div>

                {/* Mobile */}
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Mobile Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleInputChange}
                    required
                    pattern="[0-9]{10}"
                    className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition-all text-sm"
                    placeholder="10-digit mobile number"
                  />
                </div>

                {/* Address */}
                <div className="col-span-2">
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Address <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    required
                    rows="2"
                    className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition-all resize-none text-sm"
                    placeholder="Your complete address"
                  />
                </div>

                {/* Current Company */}
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Current Company
                  </label>
                  <input
                    type="text"
                    name="currentCompany"
                    value={formData.currentCompany}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition-all text-sm"
                    placeholder="Company name (if applicable)"
                  />
                </div>

                {/* Current Designation */}
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Current Designation
                  </label>
                  <input
                    type="text"
                    name="currentDesignation"
                    value={formData.currentDesignation}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition-all text-sm"
                    placeholder="Your current role"
                  />
                </div>

                {/* Experience */}
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Total Experience <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="experience"
                    value={formData.experience}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition-all text-sm"
                  >
                    <option value="">Select experience</option>
                    <option value="fresher">Fresher (0 years)</option>
                    <option value="1-2">1-2 years</option>
                    <option value="3-5">3-5 years</option>
                    <option value="6-10">6-10 years</option>
                    <option value="10+">10+ years</option>
                  </select>
                </div>

                {/* Notice Period */}
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Notice Period <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="noticePeriod"
                    value={formData.noticePeriod}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition-all text-sm"
                  >
                    <option value="">Select notice period</option>
                    <option value="immediate">Immediate</option>
                    <option value="15-days">15 Days</option>
                    <option value="1-month">1 Month</option>
                    <option value="2-months">2 Months</option>
                    <option value="3-months">3 Months</option>
                  </select>
                </div>

                {/* Expected Salary */}
                <div className="col-span-2">
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Expected Salary (Annual in LPA)
                  </label>
                  <input
                    type="text"
                    name="expectedSalary"
                    value={formData.expectedSalary}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition-all text-sm"
                    placeholder="e.g., 5-7 LPA"
                  />
                </div>

                {/* Resume Upload */}
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Upload Resume <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx"
                      onChange={(e) => handleFileChange(e, 'resume')}
                      required
                      className="hidden"
                      id="resume-upload"
                    />
                    <label
                      htmlFor="resume-upload"
                      className="flex items-center justify-center gap-2 w-full px-3 py-2.5 border-2 border-dashed border-gray-300 rounded-lg hover:border-emerald-500 cursor-pointer transition-all bg-gray-50 hover:bg-emerald-50"
                    >
                      <Upload className="h-4 w-4 text-gray-500" />
                      <span className="text-xs text-gray-600 truncate">
                        {formData.resume ? formData.resume.name : 'Choose PDF/DOC file'}
                      </span>
                    </label>
                  </div>
                </div>

                {/* Photo Upload */}
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Upload Photo <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleFileChange(e, 'photo')}
                      required
                      className="hidden"
                      id="photo-upload"
                    />
                    <label
                      htmlFor="photo-upload"
                      className="flex items-center justify-center gap-2 w-full px-3 py-2.5 border-2 border-dashed border-gray-300 rounded-lg hover:border-emerald-500 cursor-pointer transition-all bg-gray-50 hover:bg-emerald-50"
                    >
                      <Upload className="h-4 w-4 text-gray-500" />
                      <span className="text-xs text-gray-600 truncate">
                        {formData.photo ? formData.photo.name : 'Choose image file'}
                      </span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex gap-3">
                <Button
                  type="button"
                  onClick={() => setShowForm(false)}
                  variant="outline"
                  className="flex-1 py-3 border-2 text-sm"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="flex-1 py-3 bg-gradient-to-r from-emerald-600 to-cyan-600 hover:from-emerald-700 hover:to-cyan-700 text-white font-semibold text-sm"
                >
                  {loading ? 'Submitting...' : 'Submit Application'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Career;
